import { useCallback, useMemo } from 'react';

import { useQuery } from '@apollo/client/react';
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { download, generateCsv, mkConfig } from 'export-to-csv';
import { FileDownIcon } from 'lucide-react';

import type { ExportDialogProps } from '@modules/_shared/components/list-toolbar/ListToolbar';
import { flatten } from '@modules/_shared/utils/flatten';
import { EXPORT_LOTS } from '@modules/listings/lotsQueries';
import { useLocale } from '@shared/hooks/locale';

const csvConfig = mkConfig({
  useKeysAsHeaders: true,
  filename: 'lots_export.csv',
});

export const ListingsExport = (props: ExportDialogProps) => {
  const { where, open, onExporterClose } = props;
  const { loading, data, error } = useQuery(EXPORT_LOTS, {
    skip: !open,
    variables: { where },
  });
  const { t } = useLocale();

  // Flatten the data for CSV export
  const lots = useMemo(() => {
    if (data == null || data.lots == null) {
      return null;
    }

    const flattenedLots = data.lots.map(lot =>
      flatten(lot, [
        'property.property_images.image.url',
        // TODO: Find a way to flatten the property_files.file.name also
        'property.property_files.file.url',
      ]),
    );

    // remove all key which include __typename
    flattenedLots.forEach(lot => {
      Object.keys(lot).forEach(key => {
        if (key.includes('__typename')) {
          delete lot[key];
        }
      });
    });

    return flattenedLots;
  }, [data]);

  // Generate and download CSV file
  const downloadCSV = useCallback(() => {
    if (lots) {
      const csv = generateCsv(csvConfig)(lots);
      download(csvConfig)(csv);
    }
    onExporterClose();
  }, [lots, onExporterClose]);

  return (
    <Dialog
      open={open}
      onClose={onExporterClose}
      aria-labelledby="loading-dialog-title"
      maxWidth="sm"
    >
      <DialogTitle id="loading-dialog-title">
        {loading ? t('Preparing Export Data') : t('Export Ready')}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Alert
            severity="info"
            icon={<CircularProgress size={16} disableShrink />}
          >
            <AlertTitle>{t('Please wait')}</AlertTitle>
            {t('We are preparing your data for download')}
          </Alert>
        ) : error ? (
          <Alert severity="error">
            <AlertTitle>{t('Error')}</AlertTitle>
            {error.message}
          </Alert>
        ) : (
          <Stack
            sx={{
              gap: 2,
            }}
          >
            <Alert severity="success">
              <AlertTitle>{t('Data is ready')}</AlertTitle>
              {t('Your data is ready for download')}
            </Alert>
            <Button
              onClick={downloadCSV}
              color="primary"
              variant="contained"
              startIcon={<FileDownIcon />}
              disabled={lots == null}
            >
              {t('Download CSV')}
            </Button>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onExporterClose} color="primary">
          {t('Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
